// §5.5 — virtual desktops / Spaces.
//
// One primitive: a list of `{ id, name, order }` and a `desktopId` on every window. Switching a
// desktop **toggles a CSS class on the host container — it never moves a DOM node** (§5.7), so
// everything here is bookkeeping over the store and the host layer does the rest.
//
// The one rule that is not bookkeeping: deleting a desktop never deletes a window. Its windows
// move to a neighbour, because losing an editor because a desktop was tidied away would be the
// same class of mistake as a destructive Trash.
import type { Desktop, Rect } from '@hangar/shared';
import type { WmStore } from './store';

export interface DesktopsService {
  list(): Desktop[];
  active(): string;
  setActive(id: string): void;
  /** Appends a desktop and returns it. The name defaults to the next free `Desktop N`. */
  create(name?: string): Desktop;
  rename(id: string, name: string): void;
  /** Move a desktop to `index` in the displayed order, closing the gap it left. */
  reorder(id: string, index: number): void;
  /**
   * Remove a desktop, moving its windows to a neighbour. Returns `false` for the last desktop —
   * a desktop is where windows live, and there must always be one.
   */
  remove(id: string): boolean;
  /** Wrap-around switching, for §6.4's `desktop.previous` / `desktop.next`. */
  step(delta: number): void;

  /**
   * §5.5 / D-032 — "the macOS skin renders a `fullscreenSpace` window as a dedicated desktop
   * containing exactly that window".
   *
   * A kernel primitive rather than a skin's, even though only one skin asks for it: it is three
   * store writes that must happen together (a desktop appears, a window moves to it, the window's
   * state and rect change), and a half-applied version of that leaves a window somewhere the user
   * cannot reach. `area` is the rect to fill — the caller's work area.
   */
  enterFullscreenSpace(winId: string, area: Rect): boolean;
  /**
   * The way back: the window returns to the desktop it came from, at the rect it had, and the
   * Space that existed only to hold it is removed.
   *
   * The origin is remembered in memory, not persisted (§5.6's shape has no room for it). A window
   * restored from a session in this state exits to the first desktop, which is somewhere the user
   * can see it — the alternative is a window with nowhere to go.
   */
  exitFullscreenSpace(winId: string): boolean;
  /** Whether a desktop exists only to hold one fullscreen window — the Spaces strip dims these. */
  isFullscreenSpace(desktopId: string): boolean;
}

export interface DesktopsOptions {
  wm: WmStore;
  /** `Desktop 1`, `Desktop 2`… Injected so a skin or a locale can rename the pattern later. */
  nameFor?: (index: number) => string;
  newId?: () => string;
}

/** Sorted by `order`, then by id so a tie is still deterministic. */
export function sortDesktops(desktops: readonly Desktop[]): Desktop[] {
  return [...desktops].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** Renumber `order` to 0..n-1 in the given sequence. */
export function densifyOrder(desktops: readonly Desktop[]): Desktop[] {
  return desktops.map((desktop, index) => ({ ...desktop, order: index }));
}

/** The desktop a removed one's windows should go to: the one before it, or the one after. */
export function neighbourOf(ordered: readonly Desktop[], id: string): Desktop | null {
  const index = ordered.findIndex((desktop) => desktop.id === id);
  if (index < 0) return null;
  return ordered[index - 1] ?? ordered[index + 1] ?? null;
}

export function createDesktopsService(options: DesktopsOptions): DesktopsService {
  const { wm } = options;
  /**
   * §5.5 / D-032 — desktop id → the window it was created for and the desktop that window came
   * from. In memory only: §5.6's persisted shape has no field for it, and inventing one would be
   * changing a schema the server already reads.
   */
  const spaces = new Map<string, { winId: string; originDesktopId: string }>();
  const nameFor = options.nameFor ?? ((index: number) => `Desktop ${index}`);
  let counter = 0;
  const newId = options.newId ?? (() => `desktop-${(counter += 1)}-${Date.now().toString(36)}`);

  const ordered = (): Desktop[] => sortDesktops(wm.getState().desktops);

  /** The lowest `Desktop N` nobody is using, so deleting the middle one does not leave a hole. */
  function freeName(): string {
    const taken = new Set(wm.getState().desktops.map((desktop) => desktop.name));
    for (let n = 1; ; n += 1) {
      const candidate = nameFor(n);
      if (!taken.has(candidate)) return candidate;
    }
  }

  return {
    list: ordered,
    active: () => wm.getState().activeDesktopId,

    setActive(id) {
      if (!wm.getState().desktops.some((desktop) => desktop.id === id)) return;
      wm.setActiveDesktop(id);
    },

    create(name) {
      const list = ordered();
      const desktop: Desktop = {
        id: newId(),
        name: name ?? freeName(),
        order: list.length,
      };
      wm.setDesktops([...list, desktop]);
      return desktop;
    },

    rename(id, name) {
      const trimmed = name.trim();
      if (trimmed.length === 0) return;
      wm.setDesktops(
        ordered().map((desktop) => (desktop.id === id ? { ...desktop, name: trimmed } : desktop)),
      );
    },

    reorder(id, index) {
      const list = ordered();
      const from = list.findIndex((desktop) => desktop.id === id);
      const moving = list[from];
      if (from < 0 || !moving) return;
      const rest = list.filter((desktop) => desktop.id !== id);
      const target = Math.max(0, Math.min(index, rest.length));
      wm.setDesktops(densifyOrder([...rest.slice(0, target), moving, ...rest.slice(target)]));
    },

    remove(id) {
      const list = ordered();
      if (list.length <= 1) return false;
      const fallback = neighbourOf(list, id);
      if (!fallback) return false;

      // Windows first: a window must never be homeless, not even for one store update.
      for (const win of wm.list()) {
        if (win.desktopId === id) wm.moveToDesktop(win.id, fallback.id);
      }
      if (wm.getState().activeDesktopId === id) wm.setActiveDesktop(fallback.id);
      wm.setDesktops(densifyOrder(list.filter((desktop) => desktop.id !== id)));
      return true;
    },

    enterFullscreenSpace(winId, area) {
      const win = wm.get(winId);
      if (!win || win.state === 'fullscreenSpace') return false;

      // The rect it had comes back on the way out, and `restore` is what reads it (§5.2).
      wm.setRestoreRect(winId, win.rect);
      const list = ordered();
      const space: Desktop = { id: newId(), name: win.title, order: list.length };
      wm.setDesktops([...list, space]);
      spaces.set(space.id, { winId, originDesktopId: win.desktopId });

      wm.moveToDesktop(winId, space.id);
      wm.setRect(winId, { ...area });
      wm.setWinState(winId, 'fullscreenSpace');
      wm.setActiveDesktop(space.id);
      wm.focus(winId);
      return true;
    },

    exitFullscreenSpace(winId) {
      const win = wm.get(winId);
      if (!win || win.state !== 'fullscreenSpace') return false;

      const spaceId = win.desktopId;
      const record = spaces.get(spaceId);
      const list = ordered();
      // A session restored in this state has no record; the first desktop is somewhere the user
      // can see it, which beats leaving the window on a Space that is about to be removed.
      const home = record?.originDesktopId ?? list[0]?.id ?? spaceId;

      // The window moves **first**, so it is never on a desktop that does not exist.
      wm.moveToDesktop(winId, home);
      // `restore` puts back the rect `enterFullscreenSpace` saved and the `normal` state with it.
      wm.restore(winId);
      wm.setActiveDesktop(home);

      if (spaces.has(spaceId) && list.length > 1) {
        spaces.delete(spaceId);
        // Any window that somehow ended up here goes home too — `remove` is what guarantees that,
        // and it refuses to strand one.
        wm.setDesktops(densifyOrder(list.filter((desktop) => desktop.id !== spaceId)));
        for (const other of wm.list()) {
          if (other.desktopId === spaceId) wm.moveToDesktop(other.id, home);
        }
      }
      wm.focus(winId);
      return true;
    },

    isFullscreenSpace(desktopId) {
      return spaces.has(desktopId);
    },

    step(delta) {
      const list = ordered();
      if (list.length <= 1) return;
      const current = list.findIndex((desktop) => desktop.id === wm.getState().activeDesktopId);
      const from = current < 0 ? 0 : current;
      const next = list[(((from + delta) % list.length) + list.length) % list.length];
      if (next) wm.setActiveDesktop(next.id);
    },
  };
}
